import { Button, Input, Modal, Skeleton, Text } from "@marketplace/ui";
import { type FC, lazy, Suspense } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IoSend } from "react-icons/io5";

import { useSendAccessForm } from "../../hooks/forms/use-send-access-form";

const SendAccessRichTextEditor = lazy(() =>
  import("./send-access-rich-text-editor").then((mod) => ({
    default: mod.SendAccessRichTextEditor
  }))
);

type SendAccessModalProps = {
  opened: boolean;
  onClose: () => void;
};

export const SendAccessModal: FC<SendAccessModalProps> = ({
  opened,
  onClose
}) => {
  const { t } = useTranslation("order", {
    keyPrefix: "orderListPage.sendAccessModal"
  });
  const {
    handleSubmit,
    formState: { errors },
    isPending,
    control,
    watch
  } = useSendAccessForm();

  const credentialsValue = watch("credentials");
  const isCredentialsEmpty =
    !credentialsValue || credentialsValue.replace(/<[^>]*>/g, "").trim() === "";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text type="heading">{t("title")}</Text>}
      classNames={{
        header: "!p-6 !min-h-fit",
        body: "!p-6 !pt-0"
      }}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Controller
          name="credentials"
          control={control}
          render={({ field }) => (
            <Suspense
              fallback={
                <Input.Wrapper
                  label={t("labels.credentials")}
                  error={errors.credentials?.message}
                >
                  <Skeleton height={217} radius={8} />
                </Input.Wrapper>
              }
            >
              <SendAccessRichTextEditor
                value={field.value}
                onChange={field.onChange}
                label={t("labels.credentials")}
                error={errors.credentials?.message}
              />
            </Suspense>
          )}
        />

        <Button
          variant="primary"
          loading={isPending}
          disabled={isCredentialsEmpty}
          rightSection={<IoSend />}
          type="submit"
          onClick={handleSubmit}
          fullWidth
        >
          {t("labels.sendAccess")}
        </Button>
      </form>
    </Modal>
  );
};
